import { callGateway, type CallerIdentity } from "./gateway";
import { config } from "../config";

/**
 * The applicant's own answer to "what kind of operator are you?" --
 * GET/PUT /access/operator/{NameSpace}/applicant/type (radaccess 08b433e+).
 *
 * Owner 2026-10-02: sign-up asks only name, mobile and password; the type is
 * asked once the operator has registered and signed in.
 *
 * ⛔ radaccess binds the row to the token subject and to the applicant.<id>
 * namespace; nothing in this body names a row. Only role `applicant` holds the
 * resource, and only an un-reviewed sign-up can change its answer (409 after).
 */

export type ApplicantOperatorType = "ISP" | "VNO" | "OPERATOR" | "NETWORK_OPERATOR";

export interface ApplicantType {
  /** "" until the applicant answers. */
  operatortype: ApplicantOperatorType | "";
  editable: boolean;
}

/**
 * The four answers, in the order shown. The wording is the operator's own
 * situation, not the regulator's class name, because the person answering is
 * deciding which describes them.
 *
 * 🔑 MIRRORS radaccess model.ApplicantSelectableTypes. CLOUD_TENANT is a
 * separate product; PIN and FRANCHISE are set only by a parent's kind.
 */
export const APPLICANT_TYPE_CHOICES: ReadonlyArray<{ value: ApplicantOperatorType; title: string; detail: string }> = [
  {
    value: "ISP",
    title: "Internet service provider (ISP)",
    detail: "You hold your own ISP or telecom licence and sell internet to subscribers under it.",
  },
  {
    value: "VNO",
    title: "Virtual network operator (VNO)",
    detail: "You hold a VNO licence and sell internet over another licensed network.",
  },
  {
    value: "OPERATOR",
    title: "Reseller (no licence)",
    detail: "You sell internet to subscribers under another provider's licence — your upstream provider.",
  },
  {
    value: "NETWORK_OPERATOR",
    title: "Network operator",
    detail:
      "You run a client's network — a campus, hostel or office. The client pays for the internet " +
      "connection; you bill the client for managing it, never the people who use it.",
  },
];

const path = (ns: string) => `/operator/${encodeURIComponent(ns)}/applicant/type`;

export const getApplicantType = (i: CallerIdentity, ns: string) =>
  callGateway<ApplicantType>(i, config().gatewayBase, path(ns));

export const setApplicantType = (i: CallerIdentity, ns: string, operatortype: ApplicantOperatorType) =>
  callGateway<ApplicantType>(i, config().gatewayBase, path(ns), { method: "PUT", body: { operatortype } });
